import api from './api';

// ===================================================================
// Typen (alle Felder optional/nullable - defensives Parsing!)
// Backend: /api/community/lobbies bzw. /api/community/lobby/:id
// ===================================================================

export type LobbyStatus = 'open' | 'full' | 'closed';

export interface LobbyMember {
  userId?: string | null;
  userName?: string | null;
  userImage?: string | null;
  joinedAt?: number | null;
  [key: string]: any;
}

export interface LobbyMessage {
  id?: string | null;
  senderId?: string | null;
  senderName?: string | null;
  senderImage?: string | null;
  content?: string | null;
  timestamp?: number | null;
  [key: string]: any;
}

export interface Lobby {
  id?: string | null;
  name?: string | null;
  description?: string | null;
  gameMode?: string | null;
  platform?: string | null;
  maxMembers?: number | null;
  isPublic?: boolean | null;
  status?: LobbyStatus | null;
  scheduledAt?: number | null;
  tags?: string[] | null;
  ownerId?: string | null;
  ownerName?: string | null;
  ownerImage?: string | null;
  members?: LobbyMember[] | null;
  messages?: LobbyMessage[] | null;
  createdAt?: number | null;
  updatedAt?: number | null;
  [key: string]: any;
}

export interface LobbiesResponse {
  lobbies: Lobby[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateLobbyPayload {
  name: string;
  description?: string | null;
  gameMode?: string | null;
  platform?: string | null;
  maxMembers?: number | null;
  isPublic?: boolean | null;
  scheduledAt?: number | null;
  tags?: string[] | null;
}

/** Gängige Spielmodi für Selects (Frontend-seitige Auswahl, Backend akzeptiert freie Strings) */
export const LOBBY_GAME_MODES = [
  '501',
  '301',
  'Cricket',
  'Around the Clock',
  'Training',
  'Sonstiges',
] as const;

// ===================================================================
// Defensive Parser
// ===================================================================

function parseMember(data: any): LobbyMember | null {
  if (!data || typeof data !== 'object') return null;
  return {
    ...data,
    userId: data.userId ?? null,
    userName: data.userName ?? null,
    userImage: data.userImage ?? null,
    joinedAt: typeof data.joinedAt === 'number' ? data.joinedAt : null,
  };
}

function parseMessage(data: any): LobbyMessage | null {
  if (!data || typeof data !== 'object') return null;
  return {
    ...data,
    id: data.id ?? null,
    senderId: data.senderId ?? null,
    senderName: data.senderName ?? null,
    senderImage: data.senderImage ?? null,
    content: data.content ?? null,
    timestamp: typeof data.timestamp === 'number' ? data.timestamp : null,
  };
}

function parseStatus(value: any): LobbyStatus | null {
  if (value === 'open' || value === 'full' || value === 'closed') return value;
  return null;
}

export function parseLobby(data: any): Lobby | null {
  if (!data || typeof data !== 'object') return null;
  const membersRaw = Array.isArray(data.members) ? data.members : [];
  const messagesRaw = Array.isArray(data.messages) ? data.messages : [];
  const tagsRaw = Array.isArray(data.tags) ? data.tags : [];
  return {
    ...data,
    id: data.id ?? data._id ?? null,
    name: data.name ?? null,
    description: data.description ?? null,
    gameMode: data.gameMode ?? null,
    platform: data.platform ?? null,
    maxMembers: typeof data.maxMembers === 'number' && data.maxMembers > 0 ? data.maxMembers : 8,
    isPublic: data.isPublic !== false,
    status: parseStatus(data.status),
    scheduledAt: typeof data.scheduledAt === 'number' ? data.scheduledAt : null,
    tags: tagsRaw.filter((t: any) => typeof t === 'string' && t.trim().length > 0),
    ownerId: data.ownerId ?? null,
    ownerName: data.ownerName ?? null,
    ownerImage: data.ownerImage ?? null,
    members: membersRaw
      .map(parseMember)
      .filter((m: LobbyMember | null): m is LobbyMember => m !== null),
    messages: messagesRaw
      .map(parseMessage)
      .filter((m: LobbyMessage | null): m is LobbyMessage => m !== null),
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : null,
    updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
  };
}

// ===================================================================
// Service
// ===================================================================

class LobbyService {
  /**
   * GET /api/community/lobbies
   * Lädt öffentliche Lobbies (paginiert, optional Status/Suche).
   */
  async getLobbies(params: {
    limit?: number;
    offset?: number;
    status?: LobbyStatus | string;
    search?: string;
  } = {}): Promise<LobbiesResponse> {
    const { limit = 12, offset = 0, status, search } = params;
    const query = new URLSearchParams();
    query.set('limit', String(limit));
    query.set('offset', String(offset));
    if (status && status.trim().length > 0) query.set('status', status.trim());
    if (search && search.trim().length > 0) query.set('search', search.trim());
    const res = await api.get(`/api/community/lobbies?${query.toString()}`);
    const source = res?.data?.data ?? res?.data ?? {};
    const lobbiesRaw = Array.isArray(source?.lobbies)
      ? source.lobbies
      : Array.isArray(source)
        ? source
        : [];
    const lobbies = lobbiesRaw
      .map(parseLobby)
      .filter((l: Lobby | null): l is Lobby => l !== null);
    return {
      lobbies,
      total: typeof source?.total === 'number' ? source.total : lobbies.length,
      limit: typeof source?.limit === 'number' ? source.limit : limit,
      offset: typeof source?.offset === 'number' ? source.offset : offset,
    };
  }

  /**
   * GET /api/community/lobby/:id
   * Lädt eine einzelne Lobby (Response = Lobby direkt, kein Wrapper).
   * Gibt null bei 404 zurück, wirft bei anderen Fehlern.
   */
  async getLobbyById(lobbyId: string): Promise<Lobby | null> {
    if (!lobbyId) return null;
    try {
      const res = await api.get(`/api/community/lobby/${encodeURIComponent(lobbyId)}`);
      return parseLobby(res?.data);
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  }

  /**
   * POST /api/community/lobby (auth)
   * Erstellt eine neue Lobby. Wirft bei Fehlern (Plaintext-Fehlermeldung).
   */
  async createLobby(payload: CreateLobbyPayload): Promise<Lobby | null> {
    const body: any = { name: payload.name };
    if (payload.description != null && payload.description.trim().length > 0) {
      body.description = payload.description.trim();
    }
    if (payload.gameMode != null && payload.gameMode.trim().length > 0) {
      body.gameMode = payload.gameMode.trim();
    }
    if (payload.platform != null && payload.platform.trim().length > 0) {
      body.platform = payload.platform.trim();
    }
    if (typeof payload.maxMembers === 'number' && payload.maxMembers > 0) {
      body.maxMembers = payload.maxMembers;
    }
    if (typeof payload.isPublic === 'boolean') body.isPublic = payload.isPublic;
    if (typeof payload.scheduledAt === 'number' && payload.scheduledAt > 0) {
      body.scheduledAt = payload.scheduledAt;
    }
    if (Array.isArray(payload.tags) && payload.tags.length > 0) {
      body.tags = payload.tags;
    }
    const res = await api.post('/api/community/lobby', body);
    return parseLobby(res?.data);
  }

  /**
   * PUT /api/community/lobby/:id (auth, nur Owner)
   * Aktualisiert eine Lobby.
   */
  async updateLobby(lobbyId: string, payload: Partial<CreateLobbyPayload> & { status?: LobbyStatus }): Promise<Lobby | null> {
    const res = await api.put(`/api/community/lobby/${encodeURIComponent(lobbyId)}`, payload);
    return parseLobby(res?.data);
  }

  /**
   * POST /api/community/lobby/:id/join (auth)
   * Tritt einer Lobby bei. 400 mit Plaintext-Fehler (z.B. "Lobby ist voll").
   */
  async joinLobby(lobbyId: string): Promise<Lobby | null> {
    const res = await api.post(`/api/community/lobby/${encodeURIComponent(lobbyId)}/join`, {});
    return parseLobby(res?.data);
  }

  /**
   * POST /api/community/lobby/:id/leave (auth)
   * Verlässt eine Lobby.
   */
  async leaveLobby(lobbyId: string): Promise<Lobby | null> {
    const res = await api.post(`/api/community/lobby/${encodeURIComponent(lobbyId)}/leave`, {});
    return parseLobby(res?.data);
  }

  /**
   * POST /api/community/lobby/:id/message (auth, nur Mitglieder)
   * Sendet eine Chat-Nachricht (1–500 Zeichen).
   */
  async sendMessage(lobbyId: string, content: string): Promise<Lobby | null> {
    const res = await api.post(`/api/community/lobby/${encodeURIComponent(lobbyId)}/message`, {
      content,
    });
    return parseLobby(res?.data);
  }

  /**
   * DELETE /api/community/lobby/:id (auth, nur Owner)
   * Löscht eine Lobby.
   */
  async deleteLobby(lobbyId: string): Promise<void> {
    await api.delete(`/api/community/lobby/${encodeURIComponent(lobbyId)}`);
  }

  // =================================================================
  // Helper
  // =================================================================

  /** Deutsches Status-Label */
  getStatusLabel(status?: LobbyStatus | string | null): string {
    switch (status) {
      case 'open': return 'Offen';
      case 'full': return 'Voll';
      case 'closed': return 'Geschlossen';
      default: return 'Offen';
    }
  }

  /** Farb-Hex für den Status (für Badges) */
  getStatusColor(status?: LobbyStatus | string | null): string {
    switch (status) {
      case 'open': return '#3fced1';
      case 'full': return '#f5a524';
      case 'closed': return '#f31260';
      default: return '#3fced1';
    }
  }

  /** Ist der User Mitglied der Lobby (Owner zählt als Mitglied, wenn in members enthalten)? */
  isMember(lobby?: Lobby | null, userId?: string | null): boolean {
    if (!lobby || !userId) return false;
    const members = Array.isArray(lobby.members) ? lobby.members : [];
    return members.some((m) => m?.userId === userId);
  }

  /** Ist der User der Owner der Lobby? */
  isOwner(lobby?: Lobby | null, userId?: string | null): boolean {
    if (!lobby || !userId) return false;
    return lobby.ownerId === userId;
  }

  /** Anzahl der Mitglieder */
  getMemberCount(lobby?: Lobby | null): number {
    return Array.isArray(lobby?.members) ? lobby!.members!.length : 0;
  }

  /**
   * Formatiert den geplanten Zeitpunkt (ms) deutsch:
   * "Heute, 19:30" | "Morgen, 19:30" | "Mi., 05.07., 19:30"
   */
  formatScheduledAt(timestamp?: number | null): string {
    if (!timestamp || timestamp <= 0) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((day.getTime() - today.getTime()) / 86400000);
    if (diffDays === 0) return `Heute, ${time} Uhr`;
    if (diffDays === 1) return `Morgen, ${time} Uhr`;
    try {
      const dateLabel = date.toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
      return `${dateLabel}, ${time} Uhr`;
    } catch (_) {
      return time;
    }
  }

  /** Fehlermeldung aus axios-Fehler extrahieren (Backend sendet Plaintext) */
  extractErrorMessage(error: any, fallback: string): string {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (typeof data?.message === 'string' && data.message.trim().length > 0) return data.message;
    return fallback;
  }
}

const lobbyService = new LobbyService();
export default lobbyService;
